import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Timeline,
  Descriptions,
  message,
  Popconfirm,
  Typography,
  Alert,
  DatePicker,
  Switch,
  Collapse,
  Row,
  Col,
  Divider,
  Tooltip,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { stageStartAssignmentsRequiredMessage, hasRequiredStageStartAssignments } from '@/utils/stageStartAssignments';

function formatStageDateTime(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '-';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}
import {
  HistoryOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  RollbackOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import {
  useWorkOrderStages,
  useCurrentStage,
  useCreateStage,
  useUpdateStage,
  useStartStage,
  useCompleteStage,
  useRequestInspection,
  useResumeStage,
  useRevertStageToPending,
  useStageDimensions,
  useAddStageDimension,
  useStageMasters,
  useWorkstations,
  useShifts,
  useUsers,
  useCheckWorkOrderBlocked,
  useLatestStageInspectionSummary,
} from '../../hooks/useQCWorkflow';
import { WorkOrderStage, WorkOrderStageDimension } from '../../services/qc-workflow.service';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import EodOperationWorkLogDrawer from '@/components/manufacturing/eod-close/EodOperationWorkLogDrawer';

const { TextArea } = Input;
const { Text } = Typography;
const { Panel } = Collapse;

interface WorkOrderStagesProps {
  workOrderId: string;
  lineItemId?: string;
  /**
   * When true, hide stages linked to BOQ line operations (shown elsewhere on Operations).
   * Still allows adding/managing manual stages for the line.
   */
  manualStagesOnly?: boolean;
}

const StageDimensionsExpandSection: React.FC<{
  stageId: string;
  onAddDimension: (stageId: string) => void;
}> = ({ stageId, onAddDimension }) => {
  const { data: dims = [], isLoading } = useStageDimensions(stageId);
  const dimCols: ColumnsType<WorkOrderStageDimension> = [
    { title: 'Seq', dataIndex: 'sequence_number', width: 56 },
    { title: 'Name', dataIndex: 'dimension_name', ellipsis: true },
    { title: 'Code', dataIndex: 'dimension_code', width: 90, render: (c) => c || 'â€”' },
    {
      title: 'Nominal',
      key: 'nom',
      width: 100,
      render: (_: unknown, r) => `${r.nominal_value ?? ''} ${r.unit || ''}`.trim(),
    },
    { title: 'Tolerance', dataIndex: 'tolerance_type', width: 110 },
  ];
  return (
    <div style={{ padding: '12px 16px 12px 48px', background: '#fafafa' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Space wrap>
          <Text type="secondary">Dimensions for this stage (persisted)</Text>
          <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => onAddDimension(stageId)}>
            Add dimension
          </Button>
        </Space>
        <Table<WorkOrderStageDimension>
          size="small"
          rowKey="id"
          loading={isLoading}
          dataSource={dims}
          columns={dimCols}
          pagination={false}
          scroll={{ x: 520 }}
          locale={{ emptyText: 'No dimensions yet â€” add one with the button above or in the row actions.' }}
        />
      </Space>
    </div>
  );
};

const InspectionRejectionDetail: React.FC<{ stageId: string }> = ({ stageId }) => {
  const navigate = useNavigate();
  const { data: summary, isLoading } = useLatestStageInspectionSummary(stageId);

  if (isLoading) return <Spin size="small" style={{ margin: '8px 0' }} />;
  if (!summary) return <Text type="secondary">No inspection data available.</Text>;

  return (
    <div style={{ padding: '8px 0' }}>
      <Alert
        type="error"
        showIcon
        icon={<ExclamationCircleOutlined />}
        message={
          <Space>
            <Text strong>Inspection {summary.inspection_number} â€” {summary.result}</Text>
            {summary.inspector_name && <Text type="secondary">by {summary.inspector_name}</Text>}
          </Space>
        }
        description={
          <div>
            {summary.rejection_reason && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Rejection Reason: </Text>
                <Text>{summary.rejection_reason}</Text>
              </div>
            )}
            {summary.findings && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Findings: </Text>
                <Text>{summary.findings}</Text>
              </div>
            )}
            {summary.observations && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Observations: </Text>
                <Text>{summary.observations}</Text>
              </div>
            )}
            {summary.failed_checkpoints.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Failed Checkpoints:</Text>
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {summary.failed_checkpoints.map((cp, i) => (
                    <li key={i}>
                      <Text type="danger">{cp.type}</Text>
                      {cp.description && <Text type="secondary"> â€” {cp.description}</Text>}
                      {cp.remarks && <Text italic> ({cp.remarks})</Text>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Space size="middle" style={{ marginTop: 4 }}>
              {summary.attachment_count > 0 && (
                <Text type="secondary">{summary.attachment_count} attachment{summary.attachment_count !== 1 ? 's' : ''}</Text>
              )}
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => navigate(`/qms/inspections/${summary.inspection_id}`)}
                style={{ padding: 0 }}
              >
                View Full Inspection
              </Button>
            </Space>
          </div>
        }
        style={{ marginBottom: 0 }}
      />
    </div>
  );
};

const WorkOrderStages: React.FC<WorkOrderStagesProps> = ({
  workOrderId,
  lineItemId,
  manualStagesOnly = false,
}) => {
  const [form] = Form.useForm();
  const [stageForm] = Form.useForm();
  const [dimensionForm] = Form.useForm();
  const [isDimensionModalVisible, setIsDimensionModalVisible] = useState(false);
  const [isStageModalVisible, setIsStageModalVisible] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [editingStage, setEditingStage] = useState<WorkOrderStage | null>(null);
  const [workLogStage, setWorkLogStage] = useState<{
    id: string;
    title: string;
    notes: string | null;
    actualHours: number | null;
  } | null>(null);

  const { data: stagesRaw = [], isLoading, refetch } = useWorkOrderStages(workOrderId, lineItemId);
  const stages = useMemo(
    () =>
      manualStagesOnly
        ? stagesRaw.filter((s) => !s.boq_line_item_operation_id)
        : stagesRaw,
    [manualStagesOnly, stagesRaw]
  );
  const { data: currentStage } = useCurrentStage(workOrderId);
  const { data: stageMasters = [], isLoading: isLoadingStageMasters } = useStageMasters(false);
  const { data: workstations = [] } = useWorkstations(true);
  const { data: shifts = [] } = useShifts(true);
  const { data: users = [] } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(users), [users]);
  const userSelectOptions = useMemo(
    () =>
      users.map((u: { id: string; first_name?: string; last_name?: string; email?: string; username?: string }) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || u.email || 'Unknown user',
        email: u.email,
      })),
    [users]
  );
  const userSelectOptionsForSelect = useMemo(
    () =>
      userSelectOptions.map((o) => ({
        value: o.value,
        label: o.email ? `${o.label} (${o.email})` : o.label,
      })),
    [userSelectOptions]
  );
  const { data: isBlockedData } = useCheckWorkOrderBlocked(workOrderId);
  const isWorkOrderBlocked = isBlockedData?.isBlocked || false;
  const createStageMutation = useCreateStage();
  const updateStageMutation = useUpdateStage();
  const startStageMutation = useStartStage();
  const completeStageMutation = useCompleteStage();
  const requestInspectionMutation = useRequestInspection();
  const resumeStageMutation = useResumeStage();
  const revertToPendingMutation = useRevertStageToPending();
  const addDimensionMutation = useAddStageDimension();

  const handleOpenStageModal = (stage?: WorkOrderStage) => {
    if (stage) {
      // Edit mode
      setEditingStage(stage);
      stageForm.setFieldsValue({
        stage_master_id: stage.stage_master_id,
        sequence_number: stage.sequence_number,
        planned_start_date: stage.planned_start_date ? dayjs(stage.planned_start_date) : undefined,
        planned_end_date: stage.planned_end_date ? dayjs(stage.planned_end_date) : undefined,
        estimated_hours: stage.estimated_hours,
        assigned_to_user_id: stage.assigned_to_user_id,
        inspector_user_id: stage.inspector_user_id,
        verifier_user_id: stage.verifier_user_id,
        workstation_id: stage.workstation_id,
        shift_id: stage.shift_id,
        priority: stage.priority || 'Medium',
        is_critical_path: stage.is_critical_path || false,
        wait_for_previous: stage.wait_for_previous !== undefined ? stage.wait_for_previous : true,
        inspection_required: stage.inspection_required !== undefined ? stage.inspection_required : true,
        notes: stage.notes,
        description: stage.description,
      });
    } else {
      // Create mode — sequence after all stages on the line (incl. BOQ-linked)
      setEditingStage(null);
      stageForm.resetFields();
      const nextSeq =
        stagesRaw.reduce((max, s) => Math.max(max, Number(s.sequence_number) || 0), 0) + 1;
      stageForm.setFieldsValue({ sequence_number: nextSeq > 0 ? nextSeq : 1 });
    }
    setIsStageModalVisible(true);
  };

  const handleCreateStage = async (values: any) => {
    try {
      const payload = {
        ...values,
        planned_start_date: values.planned_start_date
          ? dayjs(values.planned_start_date).toISOString()
          : values.planned_start_date,
        planned_end_date: values.planned_end_date
          ? dayjs(values.planned_end_date).toISOString()
          : values.planned_end_date,
      };
      if (editingStage) {
        await updateStageMutation.mutateAsync({
          id: editingStage.id,
          workOrderId,
          data: payload,
        });
      } else {
        await createStageMutation.mutateAsync({
          workOrderId,
          stageData: {
            ...payload,
            work_order_line_item_id: lineItemId,
          },
        });
      }
      setIsStageModalVisible(false);
      stageForm.resetFields();
      setEditingStage(null);
      refetch();
    } catch (error) {
      console.error('Error saving stage:', error);
    }
  };

  const handleStageUserRoleChange = async (
    stage: WorkOrderStage,
    field: 'assigned_to_user_id' | 'inspector_user_id' | 'verifier_user_id',
    userId: string | null
  ) => {
    if (stage.status !== 'Pending') {
      message.warning('Operator, inspector, and verifier can only be changed while the stage is Pending (before Start).');
      return;
    }
    try {
      await updateStageMutation.mutateAsync({
        id: stage.id,
        workOrderId,
        data: { [field]: userId },
      });
      refetch();
    } catch (error) {
      console.error('Error updating stage assignment:', error);
    }
  };

  const handleStartStage = async (stageId: string) => {
    if (isWorkOrderBlocked) {
      message.error('Cannot start stage: Initial Inspection Request (IIR) must be approved first');
      return;
    }
    const stage = stages.find((s) => s.id === stageId);
    if (stage) {
      const assignmentMsg = stageStartAssignmentsRequiredMessage(stage);
      if (assignmentMsg) {
        message.error(assignmentMsg);
        return;
      }
    }

    try {
      await startStageMutation.mutateAsync({ id: stageId });
      refetch();
    } catch (error: any) {
      console.error('Error starting stage:', error);
      const errorMessage = error.response?.data?.message || 'Failed to start stage';
      message.error(errorMessage);
    }
  };

  const handleRequestInspection = async (stageId: string) => {
    try {
      await requestInspectionMutation.mutateAsync({ id: stageId });
      refetch();
    } catch (error) {
      console.error('Error requesting inspection:', error);
    }
  };

  const handleCompleteStage = async (stageId: string) => {
    try {
      await completeStageMutation.mutateAsync({ id: stageId });
      refetch();
    } catch (error) {
      console.error('Error completing stage:', error);
    }
  };

  const handleRevertToPending = async (stageId: string) => {
    try {
      await revertToPendingMutation.mutateAsync({ id: stageId });
      refetch();
    } catch (error) {
      console.error('Error reverting stage:', error);
    }
  };

  const handleAddDimension = async (values: any) => {
    if (!selectedStageId) return;
    if (!lineItemId?.trim()) {
      message.error('Line item is required to save dimensions. Use Operations from a specific work order line.');
      return;
    }
    try {
      await addDimensionMutation.mutateAsync({
        stageId: selectedStageId,
        data: { ...values, work_order_line_item_id: lineItemId },
      });
      setIsDimensionModalVisible(false);
      dimensionForm.resetFields();
    } catch (error) {
      console.error('Error adding dimension:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      'In Progress': 'processing',
      Completed: 'success',
      'On Hold': 'warning',
      Skipped: 'default',
      Paused: 'default',
      'Inspection Pending': 'orange',
      'Inspection Rejected': 'error',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      Critical: 'red',
      High: 'orange',
      Medium: 'blue',
      Low: 'default',
    };
    return colors[priority] || 'default';
  };

  const columns: ColumnsType<WorkOrderStage> = [
    {
      title: 'Sequence',
      dataIndex: 'sequence_number',
      key: 'sequence_number',
      width: 100,
    },
    {
      title: 'Unit',
      key: 'production_unit_number',
      width: 72,
      align: 'center' as const,
      render: (_: unknown, record: WorkOrderStage) =>
        record.production_unit_number != null && record.production_unit_number !== undefined
          ? String(record.production_unit_number)
          : 'â€”',
    },
    {
      title: 'Stage Code',
      dataIndex: 'stage_code',
      key: 'stage_code',
      width: 150,
    },
    {
      title: 'Stage Name',
      dataIndex: 'stage_name',
      key: 'stage_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>{priority || 'Medium'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string, record: WorkOrderStage) => {
        if (status === 'Inspection Rejected') {
          const rejectionLine = (record.notes || '').split('\n').filter(l => l.includes('[Inspection Rejected]')).pop();
          const reason = rejectionLine?.replace('[Inspection Rejected]', '').trim() || 'Inspection failed';
          return (
            <Tooltip title={reason} color="#ff4d4f">
              <Tag color="error" icon={<CloseCircleOutlined />}>{status}</Tag>
            </Tooltip>
          );
        }
        return <Tag color={getStatusColor(status)}>{status}</Tag>;
      },
    },
    {
      title: 'Operator',
      dataIndex: 'assigned_to_user_id',
      key: 'assigned_to_user_id',
      width: 180,
      render: (userId: string, record: WorkOrderStage) => {
        const label = !userId ? 'â€”' : resolveUserDisplayFromMap(userDisplayMap, userId, 'â€”');
        if (record.status !== 'Pending') {
          return label;
        }
        return (
          <Select
            showSearch
            allowClear
            placeholder="Operator"
            style={{ minWidth: 140, width: '100%' }}
            value={userId || undefined}
            options={userSelectOptionsForSelect}
            optionFilterProp="label"
            loading={updateStageMutation.isPending}
            onClick={(e) => e.stopPropagation()}
            onChange={(v) => void handleStageUserRoleChange(record, 'assigned_to_user_id', v ? String(v) : null)}
          />
        );
      },
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector_user_id',
      key: 'inspector_user_id',
      width: 180,
      render: (userId: string | undefined, record: WorkOrderStage) => {
        const label = !userId ? 'â€”' : resolveUserDisplayFromMap(userDisplayMap, userId, 'â€”');
        if (record.status !== 'Pending') {
          return label;
        }
        return (
          <Select
            showSearch
            allowClear
            placeholder="Inspector"
            style={{ minWidth: 140, width: '100%' }}
            value={userId || undefined}
            options={userSelectOptionsForSelect}
            optionFilterProp="label"
            loading={updateStageMutation.isPending}
            onClick={(e) => e.stopPropagation()}
            onChange={(v) => void handleStageUserRoleChange(record, 'inspector_user_id', v ? String(v) : null)}
          />
        );
      },
    },
    {
      title: 'Verifier',
      dataIndex: 'verifier_user_id',
      key: 'verifier_user_id',
      width: 180,
      render: (userId: string | undefined, record: WorkOrderStage) => {
        const label = !userId ? 'â€”' : resolveUserDisplayFromMap(userDisplayMap, userId, 'â€”');
        if (record.status !== 'Pending') {
          return label;
        }
        return (
          <Select
            showSearch
            allowClear
            placeholder="Verifier"
            style={{ minWidth: 140, width: '100%' }}
            value={userId || undefined}
            options={userSelectOptionsForSelect}
            optionFilterProp="label"
            loading={updateStageMutation.isPending}
            onClick={(e) => e.stopPropagation()}
            onChange={(v) => void handleStageUserRoleChange(record, 'verifier_user_id', v ? String(v) : null)}
          />
        );
      },
    },
    {
      title: 'Est. Hours',
      dataIndex: 'estimated_hours',
      key: 'estimated_hours',
      width: 110,
      align: 'center' as const,
      render: (val: number | null) => val != null ? `${val} hr${val !== 1 ? 's' : ''}` : '—',
    },
    {
      title: 'Actual Hours',
      dataIndex: 'actual_hours',
      key: 'actual_hours',
      width: 110,
      align: 'center' as const,
      render: (val: number | null | undefined, record: WorkOrderStage) => {
        if (val != null && Number.isFinite(Number(val)) && Number(val) > 0) {
          const n = Number(val);
          return `${n.toFixed(2)} hr${n !== 1 ? 's' : ''}`;
        }
        const start = record.started_date ? new Date(record.started_date) : null;
        if (!start || Number.isNaN(start.getTime())) return '—';
        const end = record.completed_date ? new Date(record.completed_date) : new Date();
        if (Number.isNaN(end.getTime())) return '—';
        const h = Math.max(0, (end.getTime() - start.getTime()) / 3_600_000);
        if (h <= 0) return '—';
        return `${h.toFixed(2)} hrs`;
      },
    },
    {
      title: 'Started',
      dataIndex: 'started_date',
      key: 'started_date',
      width: 160,
      render: (date: string) => formatStageDateTime(date),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_date',
      key: 'completed_date',
      width: 160,
      render: (date: string) => formatStageDateTime(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'Pending' && (
            <Tooltip 
              title={
                isWorkOrderBlocked 
                  ? "Cannot start: IIR (Initial Inspection Request) must be approved first" 
                  : stageStartAssignmentsRequiredMessage(record) || "Start Stage"
              }
            >
              <Button
                type="link"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleStartStage(record.id)}
                disabled={isWorkOrderBlocked || !hasRequiredStageStartAssignments(record)}
                danger={isWorkOrderBlocked}
              />
            </Tooltip>
          )}
          {record.status === 'In Progress' && (
            <>
              <Tooltip title="Back to Pending">
                <Popconfirm
                  title="Move back to Pending?"
                  description="Clears started time and the PR link on this stage. The purchase request (if any) is not deleted."
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => void handleRevertToPending(record.id)}
                >
                  <Button
                    type="link"
                    size="small"
                    icon={<RollbackOutlined />}
                    loading={revertToPendingMutation.isPending}
                  />
                </Popconfirm>
              </Tooltip>
              {record.inspection_required ? (
                <Tooltip title="Request Inspection (required before completion)">
                  <Button
                    type="link"
                    size="small"
                    icon={<PauseCircleOutlined />}
                    onClick={() => handleRequestInspection(record.id)}
                    style={{ color: '#fa8c16' }}
                    loading={requestInspectionMutation.isPending}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Complete stage (no inspection required)">
                  <Button
                    type="link"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    onClick={() => handleCompleteStage(record.id)}
                    style={{ color: '#52c41a' }}
                  />
                </Tooltip>
              )}
            </>
          )}
          {record.status === 'Inspection Pending' && record.inspection_status === 'Approved' && (
            <Tooltip title="Inspection approved â€“ Complete Stage">
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleCompleteStage(record.id)}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          )}
          {record.status === 'Inspection Rejected' && (
            <Tooltip title="Resume for rework (re-request inspection after)">
              <Button
                type="link"
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => void resumeStageMutation.mutateAsync({ id: record.id }).then(() => refetch())}
                loading={resumeStageMutation.isPending}
                style={{ color: '#cf1322' }}
              />
            </Tooltip>
          )}
          {(record.status === 'Pending' || record.status === 'In Progress') && (
            <Tooltip title="Edit Stage">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenStageModal(record)}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              lineItemId?.trim()
                ? 'Add Dimension'
                : 'Open this work order from Operations on a line item to add dimensions'
            }
          >
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              disabled={!lineItemId?.trim()}
              onClick={() => {
                setSelectedStageId(record.id);
                dimensionForm.resetFields();
                setIsDimensionModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="View operation work log">
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() =>
                setWorkLogStage({
                  id: record.id,
                  title: `${record.stage_code} — ${record.stage_name}`,
                  notes: record.notes ?? null,
                  actualHours:
                    record.actual_hours != null && Number.isFinite(Number(record.actual_hours))
                      ? Number(record.actual_hours)
                      : null,
                })
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const renderStageFormFields = () => (
    <>
      <Card size="small" title="Basic Information" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name="stage_master_id"
              label="Select Stage"
              rules={[{ required: true, message: 'Please select a stage' }]}
            >
              <Select
                placeholder="Select a stage to add"
                loading={isLoadingStageMasters}
                showSearch
                optionFilterProp="children"
                disabled={!!editingStage}
              >
                {stageMasters.map((master) => (
                  <Select.Option key={master.id} value={master.id}>
                    {master.stage_code} - {master.stage_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {editingStage ? (
              <Alert
                message={`Editing: ${editingStage.stage_code} - ${editingStage.stage_name}`}
                description="Stage Master cannot be changed after creation. You can update all other fields."
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
              />
            ) : null}
          </Col>
          <Col span={8}>
            <Form.Item
              name="sequence_number"
              label="Sequence"
              rules={[{ required: true, message: 'Required' }]}
              tooltip="Order of this stage in the workflow"
            >
              <InputNumber min={1} style={{ width: '100%' }} placeholder="1, 2, 3..." />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="inspection_required"
          label="Requires stage inspection"
          valuePropName="checked"
          tooltip="Turn off to let production complete this operation without SCI. If all operations skip inspection, Final Inspection (FIR) is also waived at work-order complete."
        >
          <Switch
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={Boolean(
              (editingStage as { boq_line_item_operation_id?: string } | null)?.boq_line_item_operation_id
            )}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.inspection_required !== cur.inspection_required}
        >
          {({ getFieldValue }) => {
            if (getFieldValue('inspection_required') !== false) return null;
            return (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Assign Operator, Inspector, and Verifier"
                  description="Stage inspection is off. Set these roles here â€” they are required to start the stage."
                />
                <Form.Item
                  name="assigned_to_user_id"
                  label="Operator"
                  rules={[{ required: true, message: 'Operator is required' }]}
                >
                  <Select
                    placeholder="Select operator"
                    showSearch
                    options={userSelectOptionsForSelect}
                    optionFilterProp="label"
                  />
                </Form.Item>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="inspector_user_id"
                      label="Inspector"
                      rules={[{ required: true, message: 'Inspector is required' }]}
                    >
                      <Select
                        placeholder="Select inspector"
                        showSearch
                        options={userSelectOptionsForSelect}
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="verifier_user_id"
                      label="Verifier"
                      rules={[{ required: true, message: 'Verifier is required' }]}
                    >
                      <Select
                        placeholder="Select verifier"
                        showSearch
                        options={userSelectOptionsForSelect}
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            );
          }}
        </Form.Item>
      </Card>

      <Collapse defaultActiveKey={['resources']} style={{ marginBottom: 16 }}>
        <Panel header="Timeline Planning" key="timeline">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="planned_start_date" label="Planned Start Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="planned_end_date" label="Planned End Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="estimated_hours" label="Estimated Hours">
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="Enter estimated hours" />
          </Form.Item>
        </Panel>

        <Panel header="Resource Assignment" key="resources">
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.inspection_required !== cur.inspection_required}
          >
            {({ getFieldValue }) => {
              if (getFieldValue('inspection_required') === false) {
                return (
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Operator, Inspector, and Verifier are set above (inspection off)."
                  />
                );
              }
              return (
                <>
                  <Form.Item name="assigned_to_user_id" label="Operator">
                    <Select
                      placeholder="Select operator"
                      showSearch
                      allowClear
                      options={userSelectOptionsForSelect}
                      optionFilterProp="label"
                    />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="inspector_user_id" label="Inspector">
                        <Select
                          placeholder="Select inspector"
                          showSearch
                          allowClear
                          options={userSelectOptionsForSelect}
                          optionFilterProp="label"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="verifier_user_id" label="Verifier">
                        <Select
                          placeholder="Select verifier"
                          showSearch
                          allowClear
                          options={userSelectOptionsForSelect}
                          optionFilterProp="label"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              );
            }}
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="workstation_id" label="Workstation">
                <Select placeholder="Select workstation" allowClear showSearch optionFilterProp="children">
                  {workstations.map((ws: { id: string; workstation_code: string; workstation_name: string }) => (
                    <Select.Option key={ws.id} value={ws.id}>
                      {ws.workstation_code} - {ws.workstation_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shift_id" label="Shift">
                <Select placeholder="Select shift" allowClear showSearch optionFilterProp="children">
                  {shifts.map((shift: { id: string; shift_code: string; shift_name: string }) => (
                    <Select.Option key={shift.id} value={shift.id}>
                      {shift.shift_code} - {shift.shift_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="Priority & Dependencies" key="priority">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Priority">
                <Select>
                  <Select.Option value="Critical">Critical</Select.Option>
                  <Select.Option value="High">High</Select.Option>
                  <Select.Option value="Medium">Medium</Select.Option>
                  <Select.Option value="Low">Low</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_critical_path" label="Critical Path" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="wait_for_previous"
            label="Wait for Previous Stage"
            valuePropName="checked"
            tooltip="Block this stage until the previous stage is completed"
          >
            <Switch />
          </Form.Item>
        </Panel>

        <Panel header="Notes & Instructions" key="notes">
          <Form.Item name="description" label="Stage Description">
            <TextArea rows={2} placeholder="Optional: Override stage master description" />
          </Form.Item>
          <Form.Item name="notes" label="Special Instructions">
            <TextArea rows={3} placeholder="Add specific instructions for this stage instance" />
          </Form.Item>
        </Panel>
      </Collapse>
    </>
  );

  const stageModal = (
    <Modal
      title={editingStage ? 'Edit Stage' : 'Add Stage to Work Order'}
      open={isStageModalVisible}
      onOk={() => stageForm.submit()}
      onCancel={() => {
        setIsStageModalVisible(false);
        stageForm.resetFields();
        setEditingStage(null);
      }}
      width={800}
      confirmLoading={createStageMutation.isPending || updateStageMutation.isPending}
      destroyOnClose
    >
      <Form
        form={stageForm}
        layout="vertical"
        onFinish={handleCreateStage}
        initialValues={{
          priority: 'Medium',
          wait_for_previous: true,
          is_critical_path: false,
          inspection_required: true,
        }}
      >
        {renderStageFormFields()}
      </Form>
    </Modal>
  );

  if (stages.length === 0) {
    return (
      <>
        <Card>
          <Alert
            message={manualStagesOnly ? 'No additional stages yet' : 'No stages added yet'}
            description={
              manualStagesOnly
                ? 'BOQ operations are listed above. Add extra stages from stage masters when needed.'
                : 'Add stages to this work order by selecting from available stage masters.'
            }
            type="info"
            showIcon
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenStageModal()}>
                Add Stage
              </Button>
            }
          />
        </Card>
        {stageModal}
      </>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* IIR Approval Warning */}
      {isWorkOrderBlocked && (
        <Alert
          message="Initial Inspection Request (IIR) Required"
          description="Stages cannot be started until the Initial Inspection Request (IIR) is approved. Please complete and approve the IIR before proceeding with production stages."
          type="warning"
          showIcon
          closable
          icon={<PauseCircleOutlined />}
        />
      )}

      {currentStage && (
        <Card title="Current Stage">
          <Descriptions column={2}>
            <Descriptions.Item label="Stage">{currentStage.stage_name}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(currentStage.status)}>{currentStage.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Started">
              {formatStageDateTime(currentStage.started_date)}
            </Descriptions.Item>
            <Descriptions.Item label="Sequence">{currentStage.sequence_number}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title="Stages Timeline">
        <Timeline>
          {stages.map((stage) => (
            <Timeline.Item
              key={stage.id}
              color={
                stage.status === 'Completed'
                  ? 'green'
                  : stage.status === 'In Progress'
                    ? 'blue'
                    : stage.status === 'Inspection Pending'
                      ? 'orange'
                      : stage.status === 'Inspection Rejected'
                        ? 'red'
                        : 'gray'
              }
            >
              <Space direction="vertical" size="small">
                <Text strong>
                  {stage.stage_name} ({stage.stage_code})
                </Text>
                <Tag color={getStatusColor(stage.status)}>{stage.status}</Tag>
                {stage.status === 'Inspection Rejected' && stage.notes
                  ? (() => {
                      const line = stage.notes
                        .split('\n')
                        .filter((l: string) => l.includes('[Inspection Rejected]'))
                        .pop();
                      const reason = line?.replace('[Inspection Rejected]', '').trim();
                      return reason ? (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {reason}
                        </Text>
                      ) : null;
                    })()
                  : null}
                {stage.started_date ? (
                  <Text type="secondary">Started: {formatStageDateTime(stage.started_date)}</Text>
                ) : null}
                {stage.completed_date ? (
                  <Text type="secondary">Completed: {formatStageDateTime(stage.completed_date)}</Text>
                ) : null}
              </Space>
            </Timeline.Item>
          ))}
        </Timeline>
      </Card>

      <Card
        title="Stages Table"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenStageModal()}>
            Add Stage
          </Button>
        }
        style={{ maxWidth: '100%', overflow: 'hidden' }}
      >
        <Table
          columns={columns}
          dataSource={stages}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          scroll={{ x: 1600, y: 400 }}
          size="small"
          bordered
          expandable={{
            expandedRowRender: (record) => (
              <div>
                {record.status === 'Inspection Rejected' ? (
                  <div style={{ padding: '8px 16px 8px 48px', background: '#fff2f0' }}>
                    <InspectionRejectionDetail stageId={record.id} />
                  </div>
                ) : null}
                <StageDimensionsExpandSection
                  stageId={record.id}
                  onAddDimension={(sid) => {
                    if (!lineItemId?.trim()) {
                      message.warning('Line item context required — use Operations under a specific line item.');
                      return;
                    }
                    setSelectedStageId(sid);
                    dimensionForm.resetFields();
                    setIsDimensionModalVisible(true);
                  }}
                />
              </div>
            ),
            rowExpandable: () => true,
            defaultExpandedRowKeys: stages
              .filter((s) => s.status === 'Inspection Rejected')
              .map((s) => s.id),
          }}
        />
      </Card>

      {stageModal}

      <Modal
        title="Add Dimension"
        open={isDimensionModalVisible}
        onOk={() => dimensionForm.submit()}
        onCancel={() => {
          setIsDimensionModalVisible(false);
          dimensionForm.resetFields();
          setSelectedStageId(null);
        }}
        confirmLoading={addDimensionMutation.isPending}
      >
        <Form form={dimensionForm} layout="vertical" onFinish={handleAddDimension}>
          <Form.Item
            name="dimension_name"
            label="Dimension Name"
            rules={[{ required: true, message: 'Please enter dimension name' }]}
          >
            <Input placeholder="e.g., Length, Width, Diameter" />
          </Form.Item>

          <Form.Item name="dimension_code" label="Dimension Code">
            <Input placeholder="Optional code" />
          </Form.Item>

          <Form.Item
            name="nominal_value"
            label="Nominal Value"
            rules={[{ required: true, message: 'Please enter nominal value' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="Enter nominal value" step={0.001} />
          </Form.Item>

          <Form.Item name="unit" label="Unit" rules={[{ required: true, message: 'Please enter unit' }]}>
            <Input placeholder="e.g., mm" />
          </Form.Item>

          <Form.Item
            name="tolerance_type"
            label="Tolerance Type"
            rules={[{ required: true, message: 'Please select tolerance type' }]}
          >
            <Select placeholder="Select tolerance type">
              <Select.Option value="Plus-Minus">Plus-Minus</Select.Option>
              <Select.Option value="Min-Max">Min-Max</Select.Option>
              <Select.Option value="Nominal">Nominal</Select.Option>
              <Select.Option value="Go-NoGo">Go-NoGo</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.tolerance_type !== currentValues.tolerance_type
            }
          >
            {({ getFieldValue }) => {
              const toleranceType = getFieldValue('tolerance_type');
              if (toleranceType === 'Plus-Minus') {
                return (
                  <>
                    <Form.Item name="tolerance_plus" label="Tolerance +">
                      <InputNumber style={{ width: '100%' }} placeholder="Enter positive tolerance" step={0.001} />
                    </Form.Item>
                    <Form.Item name="tolerance_minus" label="Tolerance -">
                      <InputNumber style={{ width: '100%' }} placeholder="Enter negative tolerance" step={0.001} />
                    </Form.Item>
                  </>
                );
              }
              if (toleranceType === 'Min-Max') {
                return (
                  <>
                    <Form.Item name="min_value" label="Minimum Value">
                      <InputNumber style={{ width: '100%' }} placeholder="Enter minimum value" step={0.001} />
                    </Form.Item>
                    <Form.Item name="max_value" label="Maximum Value">
                      <InputNumber style={{ width: '100%' }} placeholder="Enter maximum value" step={0.001} />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item name="is_critical" label="Critical Dimension" valuePropName="checked" initialValue={true}>
            <Select>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="sequence_number"
            label="Sequence Number"
            rules={[{ required: true, message: 'Please enter sequence number' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Enter sequence number" />
          </Form.Item>
        </Form>
      </Modal>

      <EodOperationWorkLogDrawer
        open={!!workLogStage}
        onClose={() => setWorkLogStage(null)}
        stageId={workLogStage?.id ?? null}
        title={workLogStage?.title ?? ''}
        fallbackNotes={workLogStage?.notes ?? null}
        fallbackHours={workLogStage?.actualHours ?? null}
        workOrderId={workOrderId}
      />
    </Space>
  );
};

export default WorkOrderStages;
